/**
 * Sidebar moderna para o Dashboard Bombons&Cia
 * Design com gradientes e ícones coloridos
 */

import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  LocalShipping as LocalShippingIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ModernSidebarProps {
  drawerWidth: number;
  open: boolean;
  onClose: () => void;
}

const ModernSidebar: React.FC<ModernSidebarProps> = ({
  drawerWidth,
  open,
  onClose
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authState } = useAuth();

  const menuItems = [
    {
      text: 'Dashboard Principal',
      icon: <DashboardIcon />,
      path: '/dashboard',
      color: theme.palette.primary.main,
      description: 'Visão geral dos KPIs'
    },
    {
      text: 'Vendas',
      icon: <BarChartIcon />,
      path: '/dashboard/sales',
      color: '#FFD93D',
      description: 'Métricas detalhadas de vendas'
    },
    {
      text: 'Lojas',
      icon: <StoreIcon />,
      path: '/dashboard/stores',
      color: theme.palette.success.main,
      description: 'Comparativo entre lojas'
    },
    {
      text: 'Vendedores',
      icon: <PersonIcon />,
      path: '/dashboard/vendedores',
      color: '#6B46C1',
      description: 'Análise por vendedor individual'
    },
    {
      text: 'Vendas Externas',
      icon: <LocalShippingIcon />,
      path: '/dashboard/vendas-externas',
      color: '#8B5CF6',
      description: 'Vendas realizadas externamente'
    },
    {
      text: 'Produtos',
      icon: <InventoryIcon />,
      path: '/dashboard/produtos',
      color: '#DC2626',
      description: 'Análise de produtos e promoções'
    }
  ];


  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, #F9FAFB 100%)`,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header da Sidebar */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.secondary.main}10 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box
              component="img"
              src="/logo_nova.png"
              alt="Bombons&Cia Logo"
              sx={{
                height: 67,
                mb: 1,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}
            />
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                textAlign: 'center',
                fontSize: '0.75rem'
              }}
            >
              Business Intelligence
            </Typography>
          </Box>
        </Box>

        <Chip
          label={`Bem-vindo, ${authState.user?.nome?.split(' ')[0] || 'Usuário'}!`}
          size="small"
          sx={{
            backgroundColor: theme.palette.primary.main + '20',
            color: theme.palette.primary.main,
            fontWeight: 600,
            width: '100%'
          }}
        />
      </Box>

      {/* Menu Principal */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {menuItems.map((item) => {
            const isSelected = location.pathname === item.path;
            
            return (
              <Tooltip
                key={item.text}
                title={item.description}
                placement="right"
                arrow
              >
                <ListItem
                  button
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    transition: 'all 0.2s ease-in-out',
                    ...(isSelected && {
                      backgroundColor: `${item.color}15`,
                      borderLeft: `4px solid ${item.color}`,
                      transform: 'translateX(4px)',
                      '&:hover': {
                        backgroundColor: `${item.color}20`,
                      }
                    }),
                    '&:hover': {
                      backgroundColor: isSelected ? `${item.color}20` : theme.palette.action.hover,
                      transform: isSelected ? 'translateX(4px)' : 'translateX(2px)',
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 44,
                      color: isSelected ? item.color : theme.palette.text.secondary,
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.4rem',
                        filter: isSelected ? `drop-shadow(0 2px 4px ${item.color}40)` : 'none'
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? item.color : theme.palette.text.primary
                    }}
                  />
                  {isSelected && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: item.color,
                        ml: 1
                      }}
                    />
                  )}
                </ListItem>
              </Tooltip>
            );
          })}
        </List>
      </Box>


      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: '#F9FAFB'
        }}
      >
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © 2025 Bombons&Cia
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={onClose}
      sx={{
        width: open && !isMobile ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: isMobile ? theme.shadows[8] : 'none',
          ...(isMobile && {
            zIndex: theme.zIndex.drawer + 1  // Sidebar abaixo do header
          })
        },
      }}
      ModalProps={{
        keepMounted: true, // Melhor performance em mobile
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default ModernSidebar;